/**
 * SUB-5.12 · FR44, FR45, NFR8. What the widget and drawer read path may cover,
 * per data source, and what it must refuse instead.
 *
 * ⚠️ THIS DECIDES WHAT IS READ AND WHAT IS REFUSED. IT BOUNDS NOTHING.
 *
 * Same standing rule as `mergedFilters.ts`, `mergedMetrics.ts`,
 * `portfolioRollup.ts` and `accountAttribution.ts`: the accounts a read may span
 * is `merged_scope_tenant_ids()` evaluated inside the policy, and nothing here
 * may ever be the reason a row is absent. A refusal removes a WIDGET, never a
 * record.
 *
 * Pure functions, so the rules are testable without a Supabase client or a DOM.
 * `widgetScope.test.ts` is the coverage; `DynamicWidgetQueryService` supplies the
 * readings and does the querying.
 *
 * ---------------------------------------------------------------------------
 * §1 · ⚠️ ONLY THREE OF THE FIVE DATA SOURCES CAN RESOLVE MERGED SCOPE, AND IT
 * IS A POLICY FACT RATHER THAN A PREFERENCE
 *
 * Measured against `pg_policies` on 2026-08-17:
 *
 * | data source | SELECT policy | resolves merged scope |
 * |---|---|---|
 * | `projects` | `projects_select` | yes, via `merged_scope_tenant_ids()` |
 * | `tasks` | `tasks_select` | yes |
 * | `notifications` | `notifications_select` | yes |
 * | `appointments` | `appointments_select` | **no**, `= get_current_user_tenant_id()` |
 * | `contracts` | `contracts_select` | **no**, same |
 *
 * ⚠️ **WIDENING THE CLIENT FILTER OVER THE LAST TWO WOULD PRODUCE A LIE, NOT AN
 * ERROR.** A read is bounded by the NARROWER of the two layers, so
 * `.in('tenant_id', [...])` over `appointments` in merged scope returns the
 * anchor's rows and nothing else, with no error anywhere. The widget would carry
 * an All Accounts heading over one account's numbers. That is precisely the
 * "silently returns a subset of what they believe they are looking at" NFR8
 * forbids, so those two are REFUSED in merged scope rather than widened.
 *
 * It costs nothing today: all 229 widgets carrying a `dataSource` are `projects`,
 * measured live the same day. It costs nothing tomorrow either, because the
 * refusal is renderable and names the source.
 *
 * ---------------------------------------------------------------------------
 * §2 · ⚠️ FR45 IS SATISFIED BY CONSTRUCTION, NOT BY CARE
 *
 * > the 236 existing widgets must keep the query path they have
 *
 * `scopeFilterFor` returns a `single` reading whenever the reach is one account,
 * and the service then emits the same `.eq('tenant_id', id)` it always did. The
 * `.in()` form is reachable only with more than one account in reach. So every
 * existing widget emits a byte-identical query, and that is a property of this
 * function rather than a thing a reviewer has to check.
 *
 * ---------------------------------------------------------------------------
 * §3 · ⚠️ FAILURE NARROWS. IT NEVER WIDENS.
 *
 * The same direction as `fetchScopeTenantIds` and `notificationService`'s
 * `bellScopeTenantIds`, and for the same reason: a merged read assembled because
 * a status call failed is the one direction there is no recovering from.
 */

import type { DataSource } from '@/types/dashboard';

/**
 * The data sources whose `SELECT` policy resolves `merged_scope_tenant_ids()`.
 *
 * ⚠️ MEASURED FROM `pg_policies`, NOT INFERRED FROM THE TABLE NAME. Adding a
 * source here without re-reading the live policy is how a widget comes to claim
 * a coverage it does not have. See the header's table for the reading and its
 * date.
 */
export const MERGED_SCOPE_DATA_SOURCES: readonly DataSource[] = [
  'projects',
  'tasks',
  'notifications',
];

export function dataSourceResolvesMergedScope(dataSource: DataSource): boolean {
  return MERGED_SCOPE_DATA_SOURCES.includes(dataSource);
}

/**
 * ⚠️ LOAD-BEARING COPY, NOT CHROME. This is the only place the product explains
 * why a widget that obviously could be counted is not on an All Accounts page.
 * It is the widget counterpart of `ACCOUNT_SCOPED_METRICS_NOTE` and
 * `ACCOUNT_SCOPED_SECTION_NOTE`, and `widgetScope.test.ts` asserts on it so a
 * tidy-up of "just wording" fails a test.
 */
export function dataSourceRefusalReason(dataSource: DataSource): string {
  return `${DATA_SOURCE_LABELS[dataSource]} can only be read one account at a time, so this widget would show the parent account on a page covering all of them. Open one account to see it.`;
}

const DATA_SOURCE_LABELS: Record<DataSource, string> = {
  projects: 'Projects',
  appointments: 'Appointments',
  tasks: 'Tasks',
  notifications: 'Notifications',
  contracts: 'Contracts',
};

/**
 * What the read path should do for one widget, on this call.
 *
 * ⚠️ THREE OUTCOMES, NEVER TWO. Folding `refused` into `single` would render the
 * anchor's rows under an All Accounts heading, which is the exact defect this
 * module exists to prevent, and folding it into an empty `merged` would render
 * "no data" for a widget that has plenty.
 */
export type WidgetReadScope =
  | { kind: 'single'; tenantId: string }
  | { kind: 'merged'; tenantIds: string[] }
  | { kind: 'refused'; reason: string };

/**
 * The accounts this widget's read may cover, or the reason it may not run.
 *
 * `reachTenantIds` is what the server said this session can read across, already
 * narrowed by the caller on a failed status call. `fallbackTenantId` is the
 * account the caller already holds, used when the reach could not be resolved at
 * all.
 */
export function scopeFilterFor(
  dataSource: DataSource,
  reachTenantIds: readonly string[],
  fallbackTenantId: string | null,
): WidgetReadScope {
  const reach = reachTenantIds.filter((id) => typeof id === 'string' && id.length > 0);

  // §3. Nothing resolved. Narrow to what we already hold, or refuse outright
  // rather than read across an unknown set.
  if (reach.length === 0) {
    return fallbackTenantId
      ? { kind: 'single', tenantId: fallbackTenantId }
      : {
          kind: 'refused',
          reason:
            'Your account could not be determined, so this figure would not be a complete view. Reload and try again.',
        };
  }

  // §2. One account in reach is single-account scope, whatever the mode is
  // called. The emitted query is unchanged.
  if (reach.length === 1) return { kind: 'single', tenantId: reach[0] };

  // §1. More than one account in reach, and the policy cannot follow.
  if (!dataSourceResolvesMergedScope(dataSource)) {
    return { kind: 'refused', reason: dataSourceRefusalReason(dataSource) };
  }

  return { kind: 'merged', tenantIds: [...reach] };
}

/**
 * SUB-5.12 · AC4. ⚠️ WHY THE GROUPED-COUNT RPC IS NOT CALLED IN MERGED SCOPE.
 *
 * `widget_count_grouped(p_table_name, p_group_by_column, p_tenant_id, p_filters)`
 * is `SECURITY DEFINER`, granted to `authenticated`, and opens with
 *
 *     IF auth.uid() IS NOT NULL
 *        AND p_tenant_id IS DISTINCT FROM get_current_user_tenant_id()
 *     THEN RAISE EXCEPTION 'Not authorized for tenant %' ...
 *
 * Read from `pg_proc` on 2026-08-17. It authorizes its own parameter, which is
 * F-1's rule kept, and that is exactly what makes it unusable here:
 *
 *   - a SUB-ACCOUNT id raises 42501;
 *   - the ANCHOR id, which is what `getCurrentTenantId()` returns in merged
 *     scope, passes the guard and then runs `WHERE p.tenant_id = p_tenant_id`,
 *     returning the anchor's counts WITH NO ERROR AT ALL.
 *
 * ⚠️ The failure mode of the honest call is a raise; the failure mode of the
 * call the code already makes is a plausible wrong number. So merged scope takes
 * the JavaScript path, which reads rows through the widened policy.
 */
export function groupedCountUsesRpc(scope: WidgetReadScope): boolean {
  return scope.kind === 'single';
}

/**
 * The group-by fields that mean the same thing in every account.
 *
 * ⚠️ D35, AND THIS IS THE SURFACE IT BITES HARDEST. `partitionMetricsForScope`
 * already treats an unregistered METRIC as account-scoped; nothing did the same
 * for a group-by FIELD, and a group-by is what turns a portable count into a
 * chart keyed to configuration. `current_stage_id` and `pipeline_id` are the two
 * that are live — 39 and a handful of the 60 live group-bys — and both are
 * account-scoped for the reason `mergedMetrics.ts` gives for `by_stage` and
 * `by_pipeline`: the group KEYS exist in exactly one account.
 *
 * ⚠️ `assigned_sales_rep_id` IS PORTABLE AND ITS LABELS ARE NOT, which is
 * `open_work_by_member`'s `renderCaveat` in `mergedMetrics.ts`. A caller
 * rendering it must resolve names through `merged_scope_user_names`, never
 * through `.from('users')`, or group by account instead.
 */
export const PORTABLE_GROUP_BY_FIELDS: readonly string[] = [
  'tenant_id',
  'assigned_sales_rep_id',
  'assignee_id',
  'project_status',
  'project_state',
];

/**
 * ⚠️ FAILS CLOSED ON AN UNREGISTERED FIELD, for the same reason
 * `partitionMetricsForScope` and `partitionFiltersForScope` do. A chart grouped
 * by something nobody classified is a chart whose bars may each belong to a
 * different account's idea of the same word.
 */
export function groupByRefusalReason(groupBy: string): string | null {
  if (PORTABLE_GROUP_BY_FIELDS.includes(groupBy)) return null;

  if (groupBy === 'current_stage_id') {
    return 'Stages are named and ordered per account, so the same stage name is a different stage in each and the counts cannot be added together.';
  }
  if (groupBy === 'pipeline_id') {
    return 'Pipelines belong to one account, so a pipeline from one account matches nothing in another.';
  }
  return `Grouping by "${groupBy}" has not been checked against the accounts in scope, so it is shown inside one account only.`;
}

/**
 * SUB-5.12 · AC5 · F-101. Does this aggregation read rows into JavaScript?
 *
 * ⚠️ IF IT DOES, IT MUST BE PAGED, AND THIS IS F-97 IN A SECOND PLACE.
 * `executeNumericAggregation` and the checklist-filter branch of
 * `executeGroupByCount` both `await query` with no `.range()` and no `.limit()`,
 * then aggregate in JavaScript. Re-measured against the live REST API on
 * 2026-08-17:
 *
 *     content-range: 0-1999/4870
 *
 * So the aggregate is computed over at most 2,000 rows and rendered as a number
 * that looks fine. Live today only in the sense that no widget currently exceeds
 * the cap — the one numeric-aggregation widget in the product covers 760
 * projects — and live the moment a portfolio-wide figure spans 4,870.
 *
 * `count` without a group-by is exempt and must stay exempt: it is a
 * `head: true` request answered by PostgREST's `count=exact`, which is a
 * COUNT(*) on the server and not subject to the row cap.
 */
export function aggregationReadsEveryRow(
  aggregationType: string,
  groupBy: string | undefined,
  groupedCountUsesRpcPath: boolean,
): boolean {
  if (aggregationType === 'sum' || aggregationType === 'avg' || aggregationType === 'min' || aggregationType === 'max') {
    return true;
  }
  // A grouped count reads rows whenever it cannot use the RPC: the
  // checklist-JOIN fallback, and, after this story, merged scope.
  if (aggregationType === 'count' && groupBy) return !groupedCountUsesRpcPath;
  return false;
}
